import tracks from '../../data/music.json';
import type { AppDef } from '../../types/window';
import type { Track } from '../../types/content';
import {
  getMusicNow,
  nextTrack,
  onMusicChange,
  pauseMusic,
  playMusic,
  playTrackAt,
  prevTrack,
  setMusicVolume,
  setPlaylist,
  stopMusic,
} from '../../audio/AudioEngine';
import { dispatch, getState, subscribe } from '../../state/store';
import { el, button } from './dom';

const catalog = tracks as Track[];

export function formatClock(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const whole = Math.floor(sec);
  const m = Math.floor(whole / 60);
  const r = whole % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

function unlock(): void {
  dispatch({ type: 'AUDIO_UNLOCK' });
}

function statusLine(playing: boolean, hasTrack: boolean, time: number, muted: boolean): string {
  if (muted) return 'MUTED';
  if (playing) return 'PLAYING';
  if (hasTrack && time > 0) return 'PAUSED';
  return 'STOPPED';
}

function buildPlayer(): { root: HTMLElement; refresh: () => void } {
  const root = el('div', { className: 'wa-player' });

  const main = el('div', { className: 'wa-main' });
  const lcd = el('div', { className: 'wa-lcd' });
  const brand = el('div', { className: 'wa-lcd-top' });
  brand.append(
    el('span', { className: 'wa-brand', text: 'BENAMP 2.91' }),
    el('span', { className: 'wa-kbps', text: catalog.length ? '128kbps' : 'NO DISK' }),
  );
  const titleEl = el('div', { className: 'wa-title', text: catalog.length ? 'Hit Play.' : 'Insert cassette.' });
  titleEl.setAttribute('aria-live', 'polite');
  const lcdBot = el('div', { className: 'wa-lcd-bot' });
  const timeEl = el('span', { className: 'wa-time', text: '0:00 / 0:00' });
  const statusEl = el('span', { className: 'wa-status', text: 'STOPPED' });
  lcdBot.append(timeEl, statusEl);
  lcd.append(brand, titleEl, lcdBot);

  const row = el('div', { className: 'wa-row' });
  const transport = el('div', { className: 'wa-transport' });
  transport.append(
    button('|<', () => {
      unlock();
      prevTrack();
    }, 'wa-btn'),
    button('>', () => {
      unlock();
      playMusic();
    }, 'wa-btn'),
    button('||', () => pauseMusic(), 'wa-btn'),
    button('[]', () => stopMusic(), 'wa-btn'),
    button('>|', () => {
      unlock();
      nextTrack();
    }, 'wa-btn'),
  );
  transport.children[0]?.setAttribute('aria-label', 'Previous track');
  transport.children[1]?.setAttribute('aria-label', 'Play');
  transport.children[2]?.setAttribute('aria-label', 'Pause');
  transport.children[3]?.setAttribute('aria-label', 'Stop');
  transport.children[4]?.setAttribute('aria-label', 'Next track');

  const vol = el('label', { className: 'wa-vol' });
  vol.append(el('span', { text: 'VOL' }));
  const slider = document.createElement('input');
  slider.type = 'range';
  slider.min = '0';
  slider.max = '100';
  slider.step = '1';
  slider.className = 'wa-slider';
  slider.setAttribute('aria-label', 'Volume');
  slider.addEventListener('input', () => setMusicVolume(Number(slider.value) / 100));
  vol.append(slider);
  row.append(transport, vol);
  main.append(lcd, row);

  const plist = el('div', { className: 'wa-plist' });
  const head = el('div', { className: 'wa-plist-head', text: catalog.length ? `PLAYLIST — ${catalog.length}` : 'PLAYLIST — EMPTY' });
  const body = el('div', { className: 'wa-plist-body' });
  const rows: HTMLButtonElement[] = [];
  catalog.forEach((t, i) => {
    const btn = el('button', { className: 'wa-track' });
    btn.type = 'button';
    btn.append(
      el('span', { className: 'wa-num', text: String(i + 1) }),
      el('span', { className: 'wa-track-title', text: t.title }),
      el('span', { className: 'wa-track-artist', text: t.artist }),
    );
    btn.addEventListener('click', () => {
      unlock();
      playTrackAt(i);
    });
    rows.push(btn);
    body.append(btn);
  });
  plist.append(head, body);

  root.append(main, plist);

  const refresh = (): void => {
    const now = getMusicNow();
    const muted = getState().mute;
    if (now.track) {
      titleEl.textContent = `${now.track.artist} — ${now.track.title}`;
    } else {
      titleEl.textContent = catalog.length ? 'Hit Play.' : 'Insert cassette.';
    }
    timeEl.textContent = `${formatClock(now.currentTime)} / ${formatClock(now.duration)}`;
    statusEl.textContent = statusLine(now.playing, Boolean(now.track), now.currentTime, muted);
    const pct = String(Math.round(now.volume * 100));
    if (slider.value !== pct) slider.value = pct;
    rows.forEach((btn, i) => btn.classList.toggle('is-current', i === now.index));
    if (catalog.length) {
      const pos = now.index >= 0 ? now.index + 1 : 0;
      head.textContent = `PLAYLIST — ${pos}/${catalog.length}`;
    }
  };

  refresh();
  return { root, refresh };
}

export const musicApp: AppDef = {
  appId: 'music',
  title: 'Music',
  icon: 'music',
  defaultSize: { w: 308, h: 348 },
  minSize: { w: 260, h: 280 },
  singleton: true,
  mount(root) {
    setPlaylist(catalog);
    const ui = buildPlayer();
    root.replaceChildren(ui.root);
    const unsubStore = subscribe((_s, event) => {
      if (event.type === 'SET_MUTE') ui.refresh();
    });
    const unsubMusic = onMusicChange(() => ui.refresh());
    return () => {
      unsubStore();
      unsubMusic();
      root.replaceChildren();
    };
  },
};
